import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, FormControl, Select, MenuItem, TextField } from '@mui/material';
import UserAutocomplete from '../../Hooks/useUserAutocomplete';
import { ActiveStatusEdit, RegionEdit, TailscaleStatusEdit, LostStatusEdit, BrokenStatusEdit, RegistrationStatusEdit } from './DeviceEditComponents';
import { formatDeviceType, getDeviceTypeOptionsWithLegacy, normalizeDeviceType } from '../../utils/deviceTypes';
import { StatusChip } from '../../WiviInventory/components';
import SignalStrengthIndicator from './SignalStrengthIndicator';
import { getDeviceInventorySummary, getDeviceVin } from './deviceInsights';

const STATUS_CHIP_CONFIG = {
  active: { color: 'success', label: 'Active' },
  inactive: { color: 'default', label: 'Inactive' },
  pending: { color: 'warning', label: 'Pending' },
  expired: { color: 'error', label: 'Expired' },
};

const hasDisplayValue = (value) => value !== null && value !== undefined && value !== '';

const renderPlaceholder = (label = 'N/A') => (
  <Box component="span" sx={{ color: 'text.secondary' }}>
    {label}
  </Box>
);

const renderDateTimeValue = (value, formatDateTime) => (
  hasDisplayValue(value)
    ? formatDateTime(value, 'YYYY-MM-DD HH:mm:ss')
    : renderPlaceholder('N/A')
);

const renderStateChipValue = (status, emptyLabel = 'N/A') => {
  if (!hasDisplayValue(status)) {
    return renderPlaceholder(emptyLabel);
  }

  const statusInfo = STATUS_CHIP_CONFIG[status] || { color: 'default', label: status };
  return (
    <Chip
      label={statusInfo.label}
      size="small"
      color={statusInfo.color}
      variant={status === 'active' ? 'filled' : 'outlined'}
    />
  );
};

const renderBooleanChipValue = (value, activeColor = 'default') => (
  <Chip
    label={value ? 'Yes' : 'No'}
    color={value ? activeColor : 'default'}
    size="small"
  />
);

const renderPiDeviceTypeValue = (device) => {
  const pi = device?.pi;
  const deviceType = normalizeDeviceType(device?.device_type);
  const parts = [];

  if (pi) parts.push(`PI: ${pi}`);
  if (deviceType) parts.push(`Type: ${deviceType}`);

  if (parts.length === 0) {
    return renderPlaceholder('N/A');
  }

  return (
    <Box>
      {parts.map((part, index) => (
        <Typography key={index} variant="body2" sx={{ fontSize: '0.875rem' }}>
          {part}
        </Typography>
      ))}
    </Box>
  );
};

const renderRegionValue = (region) => {
  if (!region) {
    return renderPlaceholder('None');
  }

  return <Chip label={region.code} size="small" color="primary" variant="outlined" title={region.name} />;
};

const createCommonOverviewColumns = ({ enableGrouping = false, sizes = {} } = {}) => [
  {
    id: 'wivi_signal_bars',
    header: 'Signal',
    enableEditing: false,
    enableGrouping: false,
    accessorFn: (row) => getDeviceInventorySummary(row).signalBars,
    Cell: ({ row }) => {
      const summary = getDeviceInventorySummary(row.original);
      return (
        <SignalStrengthIndicator
          value={summary.signalBars}
          networkType={summary.networkType}
          deviceType={summary.signalDeviceType}
        />
      );
    },
  },
  {
    id: 'vin',
    header: 'VIN',
    enableEditing: false,
    enableGrouping,
    ...(sizes.vin ? { size: sizes.vin } : {}),
    accessorFn: (row) => getDeviceVin(row) || row.vin || '',
    Cell: ({ row }) => {
      const vin = getDeviceVin(row.original) || row.original.vin;
      return hasDisplayValue(vin) ? vin : renderPlaceholder('N/A');
    },
  },
  {
    accessorKey: 'mac_addr',
    header: 'MAC Address',
    enableEditing: false,
    enableGrouping,
    ...(sizes.mac_addr ? { size: sizes.mac_addr } : {}),
  },
  {
    accessorKey: 'display_name',
    header: 'Display Name',
    enableEditing: false,
    enableGrouping,
    Cell: ({ cell, row }) => {
      const value = cell.getValue() || row.original.vehicle_name;
      return hasDisplayValue(value) ? value : renderPlaceholder('N/A');
    },
  },
  {
    accessorKey: 'remote_device_name',
    header: 'Device',
    enableEditing: false,
    enableGrouping,
    Cell: ({ cell }) => {
      const value = cell.getValue();
      return hasDisplayValue(value) ? value : renderPlaceholder('N/A');
    },
  },
  {
    id: 'inventory_device_type',
    header: 'Device Type',
    enableEditing: false,
    enableGrouping,
    accessorFn: (row) => row.remote_device_type || row.inventory_device_type || row.device_type || '',
    Cell: ({ row }) => {
      const deviceType = normalizeDeviceType(
        row.original.remote_device_type || row.original.inventory_device_type || row.original.device_type
      );
      return hasDisplayValue(deviceType) ? deviceType : renderPlaceholder('N/A');
    },
  },
  {
    accessorKey: 'remote_fleet_name',
    header: 'Fleet',
    enableEditing: false,
    enableGrouping,
    Cell: ({ cell }) => {
      const value = cell.getValue();
      return hasDisplayValue(value) ? value : renderPlaceholder('N/A');
    },
  },
  {
    accessorKey: 'remote_organization_name',
    header: 'Organization',
    enableEditing: false,
    enableGrouping,
    Cell: ({ cell }) => {
      const value = cell.getValue();
      return hasDisplayValue(value) ? value : renderPlaceholder('N/A');
    },
  },
];

const createInventoryStateColumns = ({ formatDateTime }) => [
  {
    id: 'wivi_online',
    header: 'Online',
    enableEditing: false,
    enableGrouping: false,
    accessorFn: (row) => getDeviceInventorySummary(row).online,
    Cell: ({ row }) => {
      const value = getDeviceInventorySummary(row.original).online;
      return value === null || value === undefined
        ? renderPlaceholder('N/A')
        : <StatusChip value={value} trueLabel="Online" falseLabel="Offline" />;
    },
  },
  {
    id: 'wivi_logging',
    header: 'Logging',
    enableEditing: false,
    enableGrouping: false,
    accessorFn: (row) => getDeviceInventorySummary(row).logging,
    Cell: ({ row }) => {
      const value = getDeviceInventorySummary(row.original).logging;
      return value === null || value === undefined
        ? renderPlaceholder('N/A')
        : <StatusChip value={value} trueLabel="Logging" falseLabel="Not Logging" />;
    },
  },
  {
    id: 'wivi_running',
    header: 'Running',
    enableEditing: false,
    enableGrouping: false,
    accessorFn: (row) => getDeviceInventorySummary(row).running,
    Cell: ({ row }) => {
      const value = getDeviceInventorySummary(row.original).running;
      return value === null || value === undefined
        ? renderPlaceholder('N/A')
        : <StatusChip value={value} trueLabel="Running" falseLabel="Stopped" />;
    },
  },
  {
    id: 'wivi_last_updated',
    header: 'Inventory Updated',
    enableEditing: false,
    enableGrouping: false,
    accessorFn: (row) => getDeviceInventorySummary(row).lastUpdated,
    Cell: ({ row }) => renderDateTimeValue(getDeviceInventorySummary(row.original).lastUpdated, formatDateTime),
  },
];

const createDateColumn = ({ accessorKey, header, formatDateTime }) => ({
  accessorKey,
  header,
  enableEditing: false,
  enableGrouping: false,
  Cell: ({ cell }) => renderDateTimeValue(cell.getValue(), formatDateTime),
});

const createBooleanFlagColumn = ({ accessorKey, header, activeColor = 'default', enableEditing = false, editRenderer }) => ({
  accessorKey,
  header,
  enableEditing,
  Cell: ({ cell }) => renderBooleanChipValue(cell.getValue(), activeColor),
  ...(editRenderer ? { Edit: editRenderer } : {}),
});

const createStateColumn = ({ accessorKey, header, enableEditing = false, emptyLabel = 'N/A', editRenderer }) => ({
  accessorKey,
  header,
  enableEditing,
  Cell: ({ cell }) => renderStateChipValue(cell.getValue(), emptyLabel),
  ...(editRenderer ? { Edit: editRenderer } : {}),
});

export const useDeviceColumns = ({ formatDateTime, availableRegions, isRegionsLoading, filterOptionsQuery }) => {
  return useMemo(
    () => [
      ...createCommonOverviewColumns({ enableGrouping: true }),
      { accessorKey: 'id', header: 'ID', enableEditing: false, enableGrouping: false },
      {
        accessorKey: 'user',
        header: 'User',
        enableEditing: true,
        enableGrouping: true,
        getGroupingValue: (row) => {
          const user = row.user;
          if (!user) return 'None';
          return user.display_name || user.email || 'Unknown';
        },
        Cell: ({ cell }) => {
          const user = cell.getValue();
          if (!user) return <span style={{ color: '#999' }}>None</span>;
          return (
            <Box>
              <Typography variant="body2" fontWeight="500">
                {user.display_name || user.email}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {user.email}
              </Typography>
            </Box>
          );
        },
        Edit: ({ row, table }) => {
          const currentUser = row.original.user;
          const handleChange = (newUserObj) => {
            row._valuesCache.user_obj = newUserObj;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };

          return (
            <UserAutocomplete
              value={currentUser}
              onChange={handleChange}
              disabled={false}
              size="small"
              label=""
              placeholder="Search user..."
            />
          );
        },
      },
      {
        accessorKey: 'region',
        header: 'Region',
        enableEditing: true,
        enableGrouping: true,
        getGroupingValue: (row) => {
          const region = row.region;
          if (!region) return 'None';
          return region.code || 'Unknown';
        },
        Cell: ({ cell }) => renderRegionValue(cell.getValue()),
        Edit: ({ row, table }) => {
          const handleChange = (value) => {
            row._valuesCache.region_code = value;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };
          return (
            <RegionEdit
              row={row}
              onChange={handleChange}
              regions={availableRegions}
              isLoading={isRegionsLoading || filterOptionsQuery.isFetching}
            />
          );
        },
      },
      {
        accessorKey: 'pi',
        header: 'PI / Device Type',
        enableEditing: true,
        enableGrouping: true,
        Cell: ({ row }) => renderPiDeviceTypeValue(row.original),
        Edit: ({ row, table }) => {
          const cachedPi = row._valuesCache?.pi;
          const cachedDeviceType = row._valuesCache?.device_type;
          const originalPi = row.original.pi || '';
          const originalDeviceType = normalizeDeviceType(row.original.device_type) || row.original.device_type || '';
          const [pi, setPi] = useState(cachedPi !== undefined ? (cachedPi || '') : originalPi);
          const [deviceType, setDeviceType] = useState(cachedDeviceType !== undefined ? (cachedDeviceType || '') : originalDeviceType);
          const deviceTypeOptions = getDeviceTypeOptionsWithLegacy(deviceType);

          const handlePiChange = (e) => {
            const value = e.target.value;
            setPi(value);
            if (!row._valuesCache) row._valuesCache = {};
            row._valuesCache.pi = value || null;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };

          const handleDeviceTypeChange = (e) => {
            const value = e.target.value;
            setDeviceType(value);
            if (!row._valuesCache) row._valuesCache = {};
            row._valuesCache.device_type = value || null;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                size="small"
                label="PI"
                value={pi}
                onChange={handlePiChange}
                placeholder="Enter PI"
              />
              <FormControl size="small" fullWidth>
                <Select
                  displayEmpty
                  value={deviceType}
                  onChange={handleDeviceTypeChange}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {deviceTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          );
        },
      },
      {
        accessorKey: 'ip',
        header: 'IP Address',
        enableEditing: true,
        Cell: ({ cell }) => {
          const ip = cell.getValue();
          return hasDisplayValue(ip) ? ip : renderPlaceholder('N/A');
        },
        Edit: ({ row, table }) => {
          const cachedValue = row._valuesCache?.ip;
          const originalValue = row.original.ip || '';
          const [value, setValue] = useState(cachedValue !== undefined ? (cachedValue || '') : originalValue);

          const handleChange = (e) => {
            const newValue = e.target.value;
            setValue(newValue);
            if (!row._valuesCache) row._valuesCache = {};
            row._valuesCache.ip = newValue || null;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };

          return (
            <TextField
              size="small"
              value={value}
              onChange={handleChange}
              placeholder="Enter IP address"
              fullWidth
            />
          );
        },
      },
      ...createInventoryStateColumns({ formatDateTime }),
      createBooleanFlagColumn({
        accessorKey: 'is_active',
        header: 'Active',
        activeColor: 'success',
        enableEditing: true,
        editRenderer: ({ row, table }) => {
          const handleChange = (value) => {
            row._valuesCache.is_active = value;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };
          return <ActiveStatusEdit row={row} onChange={handleChange} />;
        },
      }),
      createStateColumn({
        accessorKey: 'device_registration_status',
        header: 'Registration Status',
        enableEditing: true,
        editRenderer: ({ row, table }) => {
          const handleChange = (value) => {
            if (!row._valuesCache) row._valuesCache = {};
            row._valuesCache.device_registration_status = value;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };
          return <RegistrationStatusEdit row={row} onChange={handleChange} />;
        },
      }),
      createDateColumn({ accessorKey: 'device_registration_expiry_date', header: 'Registration Expiry', formatDateTime }),
      createStateColumn({
        accessorKey: 'tailscale_approval_status',
        header: 'Tailscale Status',
        enableEditing: true,
        emptyLabel: '-',
        editRenderer: ({ row, table }) => {
          const handleChange = (newValue) => {
            if (!row._valuesCache) {
              row._valuesCache = {};
            }
            row._valuesCache.tailscale_approval_status = newValue;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };

          return <TailscaleStatusEdit row={row} onChange={handleChange} />;
        },
      }),
      createDateColumn({ accessorKey: 'tailscale_approval_expiry_date', header: 'Tailscale Expiry', formatDateTime }),
      createBooleanFlagColumn({
        accessorKey: 'is_lost',
        header: 'Lost',
        activeColor: 'error',
        enableEditing: true,
        editRenderer: ({ row, table }) => {
          const handleChange = (value) => {
            if (!row._valuesCache) row._valuesCache = {};
            row._valuesCache.is_lost = value;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };
          return <LostStatusEdit row={row} onChange={handleChange} />;
        },
      }),
      createBooleanFlagColumn({
        accessorKey: 'is_broken',
        header: 'Broken',
        activeColor: 'error',
        enableEditing: true,
        editRenderer: ({ row, table }) => {
          const handleChange = (value) => {
            if (!row._valuesCache) row._valuesCache = {};
            row._valuesCache.is_broken = value;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };
          return <BrokenStatusEdit row={row} onChange={handleChange} />;
        },
      }),
      createDateColumn({ accessorKey: 'last_activity', header: 'Last Activity', formatDateTime }),
      createDateColumn({ accessorKey: 'onstar_last_seen', header: 'OnStar Last Seen', formatDateTime }),
    ],
    [formatDateTime, availableRegions, isRegionsLoading, filterOptionsQuery.isFetching]
  );
};

export const useUserDeviceColumns = ({ formatDateTime }) => {
  return useMemo(
    () => [
      { accessorKey: 'id', header: 'ID', size: 80, enableEditing: false },
      ...createCommonOverviewColumns({ sizes: { mac_addr: 180, vin: 180 } }),
      {
        accessorKey: 'pi',
        header: 'PI / Device Type',
        enableEditing: false,
        Cell: ({ row }) => renderPiDeviceTypeValue(row.original),
      },
      {
        accessorKey: 'region',
        header: 'Region',
        enableEditing: false,
        Cell: ({ cell }) => renderRegionValue(cell.getValue()),
      },
      ...createInventoryStateColumns({ formatDateTime }),
      createBooleanFlagColumn({ accessorKey: 'is_lost', header: 'Lost', activeColor: 'error' }),
      createBooleanFlagColumn({ accessorKey: 'is_broken', header: 'Broken', activeColor: 'error' }),
      createStateColumn({ accessorKey: 'device_registration_status', header: 'Registration Status' }),
      createDateColumn({ accessorKey: 'device_registration_expiry_date', header: 'Registration Expiry', formatDateTime }),
      createStateColumn({ accessorKey: 'tailscale_approval_status', header: 'Tailscale Status', emptyLabel: '-' }),
      createDateColumn({ accessorKey: 'tailscale_approval_expiry_date', header: 'Tailscale Expiry', formatDateTime }),
      createDateColumn({ accessorKey: 'last_activity', header: 'Last Activity', formatDateTime }),
    ],
    [formatDateTime]
  );
};

export const useRegistrationColumns = ({ formatDateTime }) => {
  const getStatusChipColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'approved' || statusLower === 'active') {
      return { bgcolor: '#4caf50', color: 'white' };
    }
    if (statusLower === 'denied' || statusLower === 'rejected' || statusLower === 'cancelled') {
      return { bgcolor: '#f44336', color: 'white' };
    }
    if (statusLower === 'pending' || statusLower === 'submitted') {
      return { bgcolor: '#ff9800', color: 'white' };
    }
    if (statusLower === 'in_progress' || statusLower === 'under_review' || statusLower === 'processing') {
      return { bgcolor: '#2196f3', color: 'white' };
    }
    return { bgcolor: '#9e9e9e', color: 'white' };
  };

  return useMemo(
    () => [
      {
        accessorKey: 'device_detail',
        header: 'Device',
        enableGrouping: true,
        getGroupingValue: (row) => {
          const device = row.device_detail;
          return device?.mac_addr || 'N/A';
        },
        Cell: ({ cell }) => {
          const device = cell.getValue();
          if (!device) return <span style={{ color: '#999' }}>N/A</span>;
          return (
            <Typography variant="body2" fontWeight="500">
              {device.mac_addr || 'N/A'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'request_type_display',
        header: 'Type',
        Cell: ({ cell, row }) => {
          const display = cell.getValue() || row.original.registration_type_display;
          const type = row.original.request_type || row.original.registration_type;
          return (
            <Chip
              label={display || (type === 'tailscale_approval' ? 'Tailscale Approval' : 'Device Registration')}
              size="small"
              color="primary"
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ cell, row }) => {
          const status = cell.getValue();
          const statusDisplay = row.original.status_display || status;
          const chipColor = getStatusChipColor(status);
          return (
            <Chip
              label={statusDisplay || 'N/A'}
              size="small"
              sx={{
                backgroundColor: chipColor.bgcolor,
                color: chipColor.color,
                fontWeight: 'bold',
              }}
            />
          );
        },
      },
      {
        accessorKey: 'requested_by_detail',
        header: 'Requested By',
        enableGrouping: true,
        getGroupingValue: (row) => {
          const user = row.requested_by_detail;
          if (!user) return 'N/A';
          return user.display_name || user.email || 'Unknown';
        },
        Cell: ({ cell }) => {
          const user = cell.getValue();
          if (!user) return <span style={{ color: '#999' }}>N/A</span>;
          return (
            <Box>
              <Typography variant="body2" fontWeight="500">
                {user.display_name || user.email}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {user.email}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'requested_at',
        header: 'Requested Date',
        enableGrouping: false,
        Cell: ({ cell, row }) => {
          const date = cell.getValue() || row.original.requested_date;
          return date ? formatDateTime(date, 'YYYY-MM-DD HH:mm:ss') : <span style={{ color: '#999' }}>N/A</span>;
        },
      },
      {
        accessorKey: 'reviewed_by_detail',
        header: 'Reviewed By',
        enableGrouping: true,
        getGroupingValue: (row) => {
          const user = row.reviewed_by_detail || row.approved_by_detail;
          if (!user) return 'N/A';
          return user.display_name || user.email || 'Unknown';
        },
        Cell: ({ cell, row }) => {
          const user = cell.getValue() || row.original.approved_by_detail;
          if (!user) return <span style={{ color: '#999' }}>N/A</span>;
          return (
            <Box>
              <Typography variant="body2" fontWeight="500">
                {user.display_name || user.email}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {user.email}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'reviewed_at',
        header: 'Reviewed Date',
        enableGrouping: false,
        Cell: ({ cell, row }) => {
          const date = cell.getValue() || row.original.approved_date;
          return date ? formatDateTime(date, 'YYYY-MM-DD HH:mm:ss') : <span style={{ color: '#999' }}>N/A</span>;
        },
      },
      {
        accessorKey: 'completed_at',
        header: 'Completed At',
        enableGrouping: false,
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return date ? formatDateTime(date, 'YYYY-MM-DD HH:mm:ss') : <span style={{ color: '#999' }}>N/A</span>;
        },
      },
      {
        accessorKey: 'expiry_date',
        header: 'Expiry Date',
        enableGrouping: false,
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return date ? formatDateTime(date, 'YYYY-MM-DD HH:mm:ss') : <span style={{ color: '#999' }}>N/A</span>;
        },
      },
      {
        accessorKey: 'renewed_by_detail',
        header: 'Renewed By',
        enableGrouping: true,
        getGroupingValue: (row) => {
          const user = row.renewed_by_detail;
          if (!user) return 'N/A';
          return user.display_name || user.email || 'Unknown';
        },
        Cell: ({ cell }) => {
          const user = cell.getValue();
          if (!user) return <span style={{ color: '#999' }}>N/A</span>;
          return (
            <Box>
              <Typography variant="body2" fontWeight="500">
                {user.display_name || user.email}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {user.email}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'renewed_at',
        header: 'Renewed At',
        enableGrouping: false,
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return date ? formatDateTime(date, 'YYYY-MM-DD HH:mm:ss') : <span style={{ color: '#999' }}>N/A</span>;
        },
      },
      {
        accessorKey: 'requested_device_type',
        header: 'Device Type',
        Cell: ({ cell, row }) => {
          const deviceType = cell.getValue() || row.original.device_type;
          return deviceType ? formatDeviceType(deviceType) : <span style={{ color: '#999' }}>N/A</span>;
        },
      },
      {
        accessorKey: 'requested_pi',
        header: 'PI',
        Cell: ({ cell, row }) => {
          const pi = cell.getValue() || row.original.pi;
          return pi || <span style={{ color: '#999' }}>N/A</span>;
        },
      },
      {
        accessorKey: 'requested_mac_addr',
        header: 'Requested MAC',
        Cell: ({ cell }) => {
          const mac = cell.getValue();
          return mac || <span style={{ color: '#999' }}>N/A</span>;
        },
      },
      {
        accessorKey: 'requested_to_detail',
        header: 'Transfer To',
        enableGrouping: true,
        getGroupingValue: (row) => {
          const user = row.requested_to_detail;
          if (!user) return 'N/A';
          return user.display_name || user.email || 'Unknown';
        },
        Cell: ({ cell }) => {
          const user = cell.getValue();
          if (!user) return <span style={{ color: '#999' }}>N/A</span>;
          return (
            <Box>
              <Typography variant="body2" fontWeight="500">
                {user.display_name || user.email}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {user.email}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'replacement_device_detail',
        header: 'Replacement Device',
        enableGrouping: true,
        getGroupingValue: (row) => {
          const device = row.replacement_device_detail;
          return device?.mac_addr || 'N/A';
        },
        Cell: ({ cell }) => {
          const device = cell.getValue();
          if (!device) return <span style={{ color: '#999' }}>N/A</span>;
          return (
            <Typography variant="body2" fontWeight="500">
              {device.mac_addr || 'N/A'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'rejection_reason',
        header: 'Rejection Reason',
        Cell: ({ cell }) => {
          const reason = cell.getValue();
          return reason || <span style={{ color: '#999' }}>None</span>;
        },
      },
      {
        accessorKey: 'reason',
        header: 'Reason',
        Cell: ({ cell }) => {
          const reason = cell.getValue();
          return reason || <span style={{ color: '#999' }}>None</span>;
        },
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        Cell: ({ cell }) => {
          const notes = cell.getValue();
          return notes || <span style={{ color: '#999' }}>None</span>;
        },
      },
    ],
    [formatDateTime]
  );
};

